import { Button, Typography } from '@mui/material';
import { PDFDownloadLink } from '@react-pdf/renderer';
import Lottie from 'lottie-react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import successAnimation from '../../assets/plinks-success.json';
import { LandingLayout } from '../../components/Landing';
import { Loading } from '../../components/Loading';
import { PaymentErrorCard } from '../../components/PaymentErrorCard';
import { PDFReceipt } from '../../components/PDFReceipt';
import { urlHelper } from '../../helpers/urlHelper';
import { Currency } from '../../store/config/enums';
import { RootState, VerifyPaymentResponse } from '../../store/config/types';
import { paymentsService } from '../../services/payments.service';
import { getPaymentReference, getVerificationPaymentId } from './verificationPaymentId';

interface ForcedPaymentErrorPreview {
  message: string;
  paymentReference: string;
  businessName: string;
  linkDescription: string;
  amountValue: number;
  currency: Currency;
  issuerName: string;
  installments: number;
  linkReference: string;
  errorCode?: number;
  validationErrors: string[];
}

function getForcedPaymentErrorPreview(): ForcedPaymentErrorPreview | null {
  if (process.env.NODE_ENV !== 'development' || typeof window === 'undefined') {
    return null;
  }

  const searchParams = new URLSearchParams(window.location.search);

  if (searchParams.get('forcePaymentError') !== '1') {
    return null;
  }

  const parsedAmount = parseFloat(searchParams.get('amountValue') || '');
  const parsedInstallments = parseInt(searchParams.get('installments') || '', 10);
  const parsedErrorCode = parseInt(searchParams.get('errorCode') || '', 10);
  const parsedCurrency = parseInt(searchParams.get('currency') || '', 10);

  return {
    message: searchParams.get('message') || 'Ha ocurrido un error inesperado',
    paymentReference: searchParams.get('beginPaymentId') || '0c6fb9cb-1330-462e-bc45-88997ab71102',
    businessName: searchParams.get('businessName') || 'Empresa demo',
    linkDescription: searchParams.get('linkDescription') || 'Pago de servicio',
    amountValue: Number.isNaN(parsedAmount) ? 1250 : parsedAmount,
    currency: parsedCurrency === Currency.DOLAR ? Currency.DOLAR : Currency.PESO,
    issuerName: searchParams.get('issuerName') || 'Visa',
    installments: Number.isNaN(parsedInstallments) ? 1 : parsedInstallments,
    linkReference: searchParams.get('linkReference') || 'FACT-1024',
    errorCode: Number.isNaN(parsedErrorCode) ? undefined : parsedErrorCode,
    validationErrors: (searchParams.get('validationErrors') || '')
      .split('|')
      .map((value) => value.trim())
      .filter((value) => value !== ''),
  };
}

function Result() {
  const { payments, business } = useSelector((state: RootState) => ({
    payments: state.payments,
    business: state.business,
  }));
  const [ready, setReady] = useState<boolean>(false);
  const navigate = useNavigate();
  let urlVars = urlHelper.parseQueryString();
  const transactionPayment = payments.transactionPayment;
  const forcedPaymentErrorPreview = getForcedPaymentErrorPreview();
  const [verificacion, setVerificacion] = useState<VerifyPaymentResponse | null>(null);
  const paymentReference = getPaymentReference(
    verificacion?.plexoReferenceId,
    payments.confirmedPayment?.plexoReferenceId,
    verificacion?.beginPaymentId,
    urlVars.beginPaymentId,
  );

  const isAbitabPayment = transactionPayment?.instrument?.issuerName === 'Abitab';

  const suscessTitle = isAbitabPayment ? '¡Pago iniciado!' : '¡Tu pago ha sido procesado con éxito!';
  const successSubtitle = 'Su proceso ha finalizado, para salir solo debes cerrar la ventana.';
  const animation = <Lottie animationData={successAnimation} loop={false} />;
  const successContent = (
    <>
      {isAbitabPayment ? (
        <>
          <Typography className="reference" variant="h6">
            {`El código a presentar en Abitab es ${transactionPayment?.abitabCode}`}
          </Typography>
          <Typography className="reference" variant="body2">
            {`También puede realizar el pago con su número de CI ${transactionPayment?.instrument?.issuerName}`}
          </Typography>
        </>
      ) : (
        <Typography variant="subtitle2" className="content-subtitle">
          Referencia de pago: {paymentReference}
        </Typography>
      )}
      {transactionPayment?.instrument &&
        payments.confirmedPayment &&
        paymentReference &&
        ready && (
          <PDFDownloadLink
            document={
              <PDFReceipt
                type="selectedPayment"
                {...transactionPayment}
                confirmedPayment={{
                  ...payments.confirmedPayment,
                  plexoReferenceId: paymentReference,
                }}
                businessVatRate={business.business?.vatRate!}
              />
            }
            fileName={`Pago ${paymentReference}`}
            style={{ textDecoration: 'none' }}
          >
            {({ loading }) => (
              <Button variant="contained" color="primary" disabled={loading} className="download-button">
                Descargar comprobante
              </Button>
            )}
          </PDFDownloadLink>
        )}
    </>
  );

  useEffect(() => {
    if (forcedPaymentErrorPreview) {
      return;
    }

    const verificationPaymentId = getVerificationPaymentId(
      urlVars.beginPaymentId,
      payments.confirmedPayment?.plexoReferenceId,
    );

    if (verificationPaymentId) {
      paymentsService.VerifyPayment({
        beginPaymentId: verificationPaymentId,
      })
        .then((result: any) => {
          setVerificacion(result);
        })
        .catch((err: any) => {
          setVerificacion({
            beginPaymentId: verificationPaymentId,
            plexoReferenceId: null,
            errorCode: 0,
            validationErrors: [],
            isSuccess: false,
            message: err?.message || 'Error desconocido',
          });
        });
    }
  }, [payments.confirmedPayment?.plexoReferenceId, urlVars.beginPaymentId, forcedPaymentErrorPreview]);

useEffect(() => {
  if (forcedPaymentErrorPreview) {
    return;
  }

  if (
    !payments.confirmingPayment &&
    !payments.confirmPaymentSuccess &&
    payments.confirmPaymentErrorMessage === null &&
    !urlVars.beginPaymentId
  ) {
    navigate('/');
  }
}, [
  payments.confirmingPayment,
  payments.confirmPaymentSuccess,
  payments.confirmPaymentErrorMessage,
  urlVars.beginPaymentId,
  navigate,
  forcedPaymentErrorPreview,
]);

  useEffect(() => {
    if (forcedPaymentErrorPreview) {
      return;
    }

    if (!ready) {
      setTimeout(() => {
        setReady(true);
      }, 500);
    }
  }, [ready, setReady, forcedPaymentErrorPreview]);
  if (forcedPaymentErrorPreview) {
    return (
      <PaymentErrorCard
        paymentReference={forcedPaymentErrorPreview.paymentReference}
        businessName={forcedPaymentErrorPreview.businessName}
        linkDescription={forcedPaymentErrorPreview.linkDescription}
        amountValue={forcedPaymentErrorPreview.amountValue}
        currency={forcedPaymentErrorPreview.currency}
        issuerName={forcedPaymentErrorPreview.issuerName}
        installments={forcedPaymentErrorPreview.installments}
        linkReference={forcedPaymentErrorPreview.linkReference}
        technicalMessages={[forcedPaymentErrorPreview.message]}
        errorCode={forcedPaymentErrorPreview.errorCode}
        validationErrors={forcedPaymentErrorPreview.validationErrors}
      />
    );
  }
  if (payments.confirmingPayment || verificacion === null) {
    return <Loading />;
  }
  console.log('payments', payments);
  if (verificacion?.isSuccess || payments.confirmPaymentSuccess
) {
    return (
      <div className="screen-container landing">
        <LandingLayout
          title={suscessTitle}
          subtitle={successSubtitle}
          content={successContent}
          animation={animation}
        />
      </div>
    );
  }
  return (
    <PaymentErrorCard
      paymentReference={paymentReference}
      businessName={transactionPayment?.businessName}
      linkDescription={transactionPayment?.linkDescription}
      amountValue={transactionPayment?.amount?.value ?? payments.confirmedPayment?.amountValue}
      currency={transactionPayment?.amount?.currency ?? payments.confirmedPayment?.currency}
      issuerName={transactionPayment?.instrument?.issuerName}
      installments={payments.confirmedPayment?.selectedInstallment ?? transactionPayment?.selectedInstallment}
      linkReference={payments.confirmedPayment?.linkReference}
      technicalMessages={[
        verificacion?.message || '',
        payments.confirmedPayment?.message || '',
        payments.confirmedPayment?.plexoResultText || '',
        payments.confirmPaymentErrorMessage || '',
      ]}
      errorCode={verificacion?.errorCode || payments.confirmedPayment?.errorCode}
      validationErrors={verificacion?.validationErrors || payments.confirmedPayment?.validationErrors || []}
    />
  );
}


export default Result;
